import React, {useContext, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import {Input, Pagination} from 'antd'
import {LoadingOutlined} from '@ant-design/icons'
import {IoChevronDown, IoChevronUp, IoFilterOutline, IoMailOutline} from 'react-icons/io5'
import {LoginContext} from 'components/LoginContext'
import useAsync from 'modules/asyncCache/useAsync'
import {ChipGroup} from 'components/form/ModernForm'
import {familyMoment_getMyMoments_Api} from 'apis/familyMomentNotice'
import {InfoBanner, MOMENT_META, MOMENT_TYPES_ORDERED, MomentRow} from './MomentRow'
import SendMomentNoticeModal from './SendMomentNoticeModal'
import CreateCustomMomentButton from './CreateCustomMomentButton'

// Trang "Kỷ niệm" - list ĐẦY ĐỦ (không cắt 10 như teaser Trang Chủ) + filter loại/khoảng ngày +
// search tên, cho action "Gửi thông báo" từng dịp (SendMomentNoticeModal). Nút phễu + panel filter
// expand IN-FLOW có mũi tên (đúng pattern usePaginationHelper/AdvancedFormGroup đang dùng ở Music/
// Bible/Prayer) - state lọc giữ LOCAL (không qua URL query param) vì đây là panel bên trong
// DrawDialog, không phải trang riêng có thể bookmark.
const MomentsPanel = ({t}) => {
    const login = useContext(LoginContext)
    const [page, setPage] = useState(1)
    const [keyword, setKeyword] = useState('')
    const [typeFilter, setTypeFilter] = useState([])
    const [showFilter, setShowFilter] = useState(false)
    const [noticeItem, setNoticeItem] = useState(null)
    const [refreshCount, setRefreshCount] = useState(0)

    // `values` (KHÔNG phải `query`) - query chỉ dùng để thay thế placeholder ":xxx" trong URL PATH
    // (path này không có placeholder nào), còn filter/search thật sự đi vào querystring GET phải qua
    // values + paramsFields (xem callApi.js: search string build từ values[key], không phải
    // query[key]) - bug thực tế đã gặp (family_book_member_id/type/keyword không hề được gửi lên).
    const {response, isLoading} = useAsync({
        apiInfo: familyMoment_getMyMoments_Api,
        values: {type: typeFilter.join(','), keyword, page},
        deps: [login?.id, typeFilter.join(','), keyword, page, refreshCount],
    })
    const items = _.get(response, 'data.data', [])
    const meta = _.get(response, 'data.meta.pagination', {})

    if (!login) {
        return (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <p className="text-sm text-color-400">{t('login to view collection')}</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <InfoBanner>{t('moments_panel_intro')}</InfoBanner>

            <div className="flex items-center gap-2">
                <Input.Search
                    allowClear
                    placeholder={t('search by member name')}
                    onSearch={(v) => { setKeyword(v); setPage(1) }}
                    className="flex-1"
                />
                <button
                    type="button"
                    onClick={() => setShowFilter((v) => !v)}
                    className={classNames(
                        'p-2 flex items-center gap-1 rounded-lg border cursor-pointer flex-shrink-0',
                        typeFilter.length ? 'bg-family-600 border-family-600 text-white' : 'bg-white border-color-100 text-color-400'
                    )}>
                    <IoFilterOutline size={16}/>
                    {showFilter ? <IoChevronUp size={12}/> : <IoChevronDown size={12}/>}
                </button>
                <CreateCustomMomentButton t={t} onSuccess={() => setRefreshCount((c) => c + 1)}/>
            </div>

            {showFilter && (
                <div className="p-3 flex flex-col gap-2 background border border-color-50 rounded-xl mass-time-filter-advance">
                    <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">{t('filter by type')}</span>
                    <ChipGroup
                        multiple
                        options={MOMENT_TYPES_ORDERED.map((type) => ({label: MOMENT_META[type].label, value: type}))}
                        value={typeFilter}
                        onChange={(v) => { setTypeFilter(v); setPage(1) }}
                    />
                </div>
            )}

            {isLoading && !items.length ? (
                <div className="flex items-center justify-center py-8"><LoadingOutlined className="text-family-500"/></div>
            ) : items.length ? (
                <div className="flex flex-col gap-2">
                    {items.map((item, i) => (
                        <MomentRow
                            key={`${item.member_id}-${item.type}-${i}`}
                            item={item}
                            t={t}
                            action={
                                <button
                                    type="button"
                                    onClick={() => setNoticeItem(item)}
                                    title={t('send moment notice')}
                                    className={classNames(
                                        'flex-shrink-0 p-2 rounded-lg cursor-pointer border-0',
                                        item.has_pending_notice ? 'bg-green-50 text-green-600' : 'bg-family-50 text-family-700 hover:bg-family-100'
                                    )}>
                                    <IoMailOutline size={16}/>
                                </button>
                            }
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-8 text-color-400">
                    <span className="text-sm">{t('no upcoming moments found')}</span>
                </div>
            )}

            {meta.total > meta.per_page && (
                <Pagination
                    className="self-center"
                    size="small"
                    current={page}
                    pageSize={meta.per_page}
                    total={meta.total}
                    onChange={setPage}
                    showSizeChanger={false}
                />
            )}

            {noticeItem && (
                <SendMomentNoticeModal
                    item={noticeItem}
                    t={t}
                    onClose={() => setNoticeItem(null)}
                    onSuccess={() => {
                        setNoticeItem(null)
                        setRefreshCount((c) => c + 1)
                    }}
                />
            )}
        </div>
    )
}

export default MomentsPanel
